import { BacklogStatus, FlightSegment, QuoteStatus } from '@pelicargo/types';
import { modelUtils, QUOTE_BOOKABLE_STAGES } from '@pelicargo/utils';
import { useMemo } from 'react';

import { TrpcOutputs } from '../../config/trpc';
import { API } from '../../utils/apiTypes';
import { QuoteVariantWithLineItems } from '../useQuoteVariantHelper';

type UseQuoteHelper = {
  id?: number;

  /** @description Determines if the quote is in a bookable stage. Decision is driven by the API, but include various things such as completeness of the quote. */
  isBookableStage: boolean;

  /** @description Determines if the quote has a pivot weight. Determined by the quote's cw not matching the request cw. **/
  hasPivotWeight: boolean;

  /** @description Determines if the quote's rate is no longer valid. Either by quote status or quote expiry date.  */
  isExpired: boolean;

  /** @description Determines if the quote is actively backlogged. */
  isBacklogged: boolean;

  /** @description Determines if the quote is at risk for backlog. */
  hasBacklogRisk: boolean;

  /** @description Returns the FlightSegment of the quote.flight_path that has the most constrained aircraft_type */
  mostConstrainedLeg: FlightSegment;

  /** @description Returns the number of stops in the quote.flight_path */
  stopCount: number;
};

export type Quote = API.QuoteWithTotalCost | TrpcOutputs['getQuote'];

export const goToQuote = (
  quote: Pick<Quote, 'id' | 'request_id'>,
  quoteVariant?: Pick<QuoteVariantWithLineItems, 'id'>,
) => {
  const baseUrl = `/requests/${quote.request_id}/quotes/${quote.id}`;
  if (!quoteVariant?.id) return baseUrl;

  return `${baseUrl}/${quoteVariant.id}`;
};

export const useQuoteHelper = (quote: Quote): UseQuoteHelper => {
  const isBacklogged = useMemo(() => {
    return quote.backlog_status === BacklogStatus.Backlogged;
  }, [quote.backlog_status]);

  const isBookableStage = useMemo(() => {
    return QUOTE_BOOKABLE_STAGES.includes(quote.stage);
  }, [quote.stage]);

  const isExpired = useMemo(() => {
    return quote.status === QuoteStatus.Expired || quote.expires_at < new Date();
  }, [quote.status, quote.expires_at]);

  const hasBacklogRisk = useMemo(() => {
    return quote.backlog_status === BacklogStatus.AtRisk;
  }, [quote.backlog_status]);

  const hasPivotWeight = useMemo(() => {
    return quote.chargeable_weight !== quote.request.chargeable_weight;
  }, [quote.chargeable_weight, quote.request.chargeable_weight]);

  const stopCount = useMemo(() => {
    return quote.flight_path.length - 1;
  }, [quote.flight_path]);

  const mostConstrainedLeg = useMemo(() => {
    return modelUtils.flightSegments.mostConstrainingLeg(quote?.flight_path);
  }, [quote.flight_path]);

  return {
    id: quote?.id,

    isBacklogged,
    isBookableStage,
    isExpired,
    hasBacklogRisk,
    hasPivotWeight,

    mostConstrainedLeg,

    stopCount,
  };
};
